// Workflow editor — create + edit. Left: step list. Right: step detail form.
// Workflow-level fields (name, description, trigger, status) sit above the
// split. Saves via POST (new) or PUT (existing) with the full payload.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Save } from 'lucide-react';
import type {
  CreateWorkflowInput,
  UpdateWorkflowInput,
  WorkflowDefinition,
  WorkflowDetail,
  WorkflowStatus,
  WorkflowStep,
  WorkflowStepOnFailure,
} from '@qualibot/shared';
import { Card } from '@/components/primitives/Card';
import { Btn } from '@/components/primitives/Btn';
import { PageContainer, PageHeader, SectionLabel } from '@/components/PageHeader';
import {
  createWorkflow,
  getWorkflow,
  updateWorkflow,
} from '@/lib/workflows-api';
import { extractApiError } from '@/lib/api-client';
import { humanizeEnum } from '@/lib/format';

const STATUS_OPTIONS: WorkflowStatus[] = ['ACTIVE', 'PAUSED', 'DRAFT'];
const ON_FAILURE_OPTIONS: WorkflowStepOnFailure[] = ['fail', 'skip', 'route_to_human'];

interface EditorState {
  name: string;
  description: string;
  triggerType: string;
  status: WorkflowStatus;
  steps: WorkflowStep[];
}

function emptyState(): EditorState {
  return {
    name: '',
    description: '',
    triggerType: '',
    status: 'DRAFT',
    steps: [],
  };
}

function fromDetail(detail: WorkflowDetail): EditorState {
  return {
    name: detail.name,
    description: detail.description ?? '',
    triggerType: detail.triggerType,
    status: detail.status,
    steps: detail.definition?.steps ?? [],
  };
}

function makeBlankStep(index: number): WorkflowStep {
  return {
    id: `step_${index + 1}`,
    name: `Step ${index + 1}`,
    tool: '',
    description: '',
    inputs: {},
    outputs: [],
    timeoutMs: 30_000,
    retry: { attempts: 1, backoffMs: 1000 },
    onFailure: 'fail',
  };
}

export function WorkflowEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isNew = id === 'new';

  const detailQuery = useQuery({
    queryKey: ['workflow', id],
    queryFn: () => getWorkflow(id as string),
    enabled: !isNew && !!id,
  });

  const [state, setState] = useState<EditorState>(emptyState);
  const [selectedStepIdx, setSelectedStepIdx] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Hydrate local state when the detail loads.
  useEffect(() => {
    if (!isNew && detailQuery.data) {
      setState(fromDetail(detailQuery.data));
      setSelectedStepIdx((prev) => {
        if (prev != null) return prev;
        return detailQuery.data?.definition?.steps.length ? 0 : null;
      });
    }
  }, [isNew, detailQuery.data]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const definition: WorkflowDefinition = { steps: state.steps };
      if (isNew) {
        const payload: CreateWorkflowInput = {
          name: state.name.trim(),
          description: state.description.trim() || undefined,
          triggerType: state.triggerType.trim(),
          status: state.status,
          definition,
        };
        return createWorkflow(payload);
      }
      const payload: UpdateWorkflowInput = {
        name: state.name.trim(),
        description: state.description.trim() ? state.description.trim() : null,
        triggerType: state.triggerType.trim(),
        status: state.status,
        definition,
      };
      return updateWorkflow(id as string, payload);
    },
    onSuccess: (saved) => {
      setError(null);
      setSavedAt(Date.now());
      qc.invalidateQueries({ queryKey: ['workflows'] });
      qc.invalidateQueries({ queryKey: ['workflow', saved.id] });
      if (isNew) {
        navigate(`/workflows/${saved.id}`, { replace: true });
      }
    },
    onError: (err) => {
      setError(extractApiError(err));
      setSavedAt(null);
    },
  });

  if (!isNew && detailQuery.isLoading) {
    return (
      <PageContainer>
        <div className="text-[13px] text-text-3 py-10 text-center">Loading workflow…</div>
      </PageContainer>
    );
  }

  if (!isNew && detailQuery.isError) {
    return (
      <PageContainer>
        <Card>
          <div
            className="px-3 py-2 rounded-md text-[12.5px]"
            style={{ background: 'var(--err-soft)', color: 'var(--err)' }}
          >
            {extractApiError(detailQuery.error)}
          </div>
        </Card>
      </PageContainer>
    );
  }

  const headerTitle = isNew ? 'New workflow' : state.name || 'Untitled workflow';
  const headerSubtitle = `${humanizeEnum(state.status)} · trigger: ${state.triggerType || '—'}`;

  function patchStep(idx: number, patch: Partial<WorkflowStep>) {
    setState((prev) => ({
      ...prev,
      steps: prev.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));
  }

  function addStep() {
    setState((prev) => {
      const next = [...prev.steps, makeBlankStep(prev.steps.length)];
      return { ...prev, steps: next };
    });
    setSelectedStepIdx(state.steps.length);
  }

  const selectedStep =
    selectedStepIdx != null ? state.steps[selectedStepIdx] ?? null : null;

  return (
    <PageContainer>
      <PageHeader
        title={headerTitle}
        description={headerSubtitle}
        actions={
          <>
            <Btn icon={ArrowLeft} onClick={() => navigate('/workflows')}>
              Back
            </Btn>
            <Btn onClick={() => navigate(-1)}>Cancel</Btn>
            <Btn
              kind="primary"
              icon={Save}
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending}
            >
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Btn>
          </>
        }
      />

      {error && (
        <div
          className="mb-3 px-3 py-2 rounded-md text-[12.5px]"
          style={{ background: 'var(--err-soft)', color: 'var(--err)' }}
        >
          {error}
        </div>
      )}

      {savedAt && !saveMut.isPending && (
        <div
          className="mb-3 px-3 py-2 rounded-md text-[12.5px]"
          style={{ background: 'var(--ok-soft)', color: 'var(--ok)' }}
        >
          Saved
        </div>
      )}

      <Card className="mb-4">
        <div className="grid grid-cols-2 gap-3.5">
          <FieldText
            label="Name"
            value={state.name}
            onChange={(v) => setState((p) => ({ ...p, name: v }))}
            placeholder="Doc approval flow"
          />
          <FieldText
            label="Trigger"
            value={state.triggerType}
            onChange={(v) => setState((p) => ({ ...p, triggerType: v }))}
            placeholder="document.uploaded"
            mono
          />
          <div className="col-span-2">
            <FieldTextarea
              label="Description"
              value={state.description}
              onChange={(v) => setState((p) => ({ ...p, description: v }))}
              rows={3}
              placeholder="What does this workflow do?"
            />
          </div>
          <FieldSelect
            label="Status"
            value={state.status}
            options={STATUS_OPTIONS.map((s) => ({ value: s, label: humanizeEnum(s) }))}
            onChange={(v) => setState((p) => ({ ...p, status: v as WorkflowStatus }))}
          />
        </div>
      </Card>

      <div
        className="grid gap-3.5"
        style={{ gridTemplateColumns: '300px 1fr' }}
      >
        <Card pad={0}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="font-semibold text-[13.5px]">Steps</div>
            <span className="text-[12px] text-text-3">{state.steps.length}</span>
          </div>
          <div className="flex flex-col">
            {state.steps.length === 0 && (
              <div className="px-4 py-6 text-[13px] text-text-3 text-center">
                No steps yet.
              </div>
            )}
            {state.steps.map((step, idx) => {
              const isActive = idx === selectedStepIdx;
              return (
                <button
                  key={step.id || idx}
                  type="button"
                  onClick={() => setSelectedStepIdx(idx)}
                  className="text-left px-4 py-2.5 text-[13px] transition-colors border-l-2"
                  style={{
                    background: isActive ? 'var(--accent-softer)' : 'transparent',
                    borderLeftColor: isActive ? 'var(--accent)' : 'transparent',
                    color: isActive ? 'var(--text)' : 'var(--text-2)',
                  }}
                >
                  <span className="font-medium">{idx + 1}.</span>{' '}
                  <span>{step.name || <em className="text-text-3">Unnamed</em>}</span>
                </button>
              );
            })}
            <button
              type="button"
              onClick={addStep}
              className="text-left px-4 py-2.5 text-[13px] text-text-2 hover:text-text hover:bg-surface-2 border-t border-border flex items-center gap-1.5"
            >
              <Plus size={14} strokeWidth={1.7} /> Add step
            </button>
          </div>
        </Card>

        <Card>
          {selectedStep == null ? (
            <div className="py-10 text-center text-[13px] text-text-3">
              {state.steps.length === 0
                ? 'Add a step to begin.'
                : 'Select a step to edit.'}
            </div>
          ) : (
            <StepEditor
              step={selectedStep}
              onPatch={(patch) => patchStep(selectedStepIdx as number, patch)}
            />
          )}
        </Card>
      </div>
    </PageContainer>
  );
}

// ===== Step editor =====

function StepEditor({
  step,
  onPatch,
}: {
  step: WorkflowStep;
  onPatch: (patch: Partial<WorkflowStep>) => void;
}) {
  const initialInputs = useMemo(
    () => JSON.stringify(step.inputs ?? {}, null, 2),
    // We re-derive when the step identity changes, not on every keystroke,
    // so the textarea remains the source of truth while the user types.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step.id],
  );
  const [inputsText, setInputsText] = useState<string>(initialInputs);
  const [inputsErr, setInputsErr] = useState<string | null>(null);
  const [outputsText, setOutputsText] = useState<string>((step.outputs ?? []).join(', '));

  // When the user clicks a different step in the list, reset locals.
  useEffect(() => {
    setInputsText(JSON.stringify(step.inputs ?? {}, null, 2));
    setInputsErr(null);
    setOutputsText((step.outputs ?? []).join(', '));
  }, [step.id]);

  function commitInputs() {
    const raw = inputsText.trim();
    if (!raw) {
      setInputsErr(null);
      onPatch({ inputs: {} });
      setInputsText('{}');
      return;
    }
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      setInputsErr(null);
      onPatch({ inputs: parsed });
      setInputsText(JSON.stringify(parsed, null, 2));
    } catch (e) {
      setInputsErr((e as Error).message);
    }
  }

  function commitOutputs(value: string) {
    const parts = value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    onPatch({ outputs: parts });
  }

  const retry = step.retry ?? { attempts: 1, backoffMs: 1000 };

  return (
    <div className="flex flex-col gap-3.5">
      <FieldText
        label="Step name"
        value={step.name}
        onChange={(v) => onPatch({ name: v })}
        placeholder="Fetch document"
      />
      <FieldText
        label="Tool"
        value={step.tool}
        onChange={(v) => onPatch({ tool: v })}
        placeholder="docs.fetch"
        mono
      />
      <FieldTextarea
        label="Description"
        value={step.description}
        onChange={(v) => onPatch({ description: v })}
        rows={3}
        placeholder="What this step does"
      />

      <div>
        <FieldLabel>Inputs (JSON)</FieldLabel>
        <textarea
          value={inputsText}
          onChange={(e) => setInputsText(e.target.value)}
          onBlur={commitInputs}
          rows={8}
          spellCheck={false}
          className="w-full px-3 py-2.5 rounded-[10px] border border-border-strong bg-surface text-[12.5px] outline-none focus:border-accent resize-y min-h-[120px]"
          style={{ fontFamily: 'var(--font-mono)' }}
        />
        {inputsErr && (
          <div
            className="mt-1.5 px-2 py-1.5 rounded-md text-[12px]"
            style={{ background: 'var(--err-soft)', color: 'var(--err)' }}
          >
            Invalid JSON: {inputsErr}
          </div>
        )}
      </div>

      <FieldText
        label="Outputs (comma-separated)"
        value={outputsText}
        onChange={(v) => {
          setOutputsText(v);
          commitOutputs(v);
        }}
        placeholder="docId, mimeType"
        mono
      />

      <FieldNumber
        label="Timeout (ms)"
        value={step.timeoutMs ?? 0}
        onChange={(n) => onPatch({ timeoutMs: n })}
      />

      <SectionLabel>Retry</SectionLabel>
      <div className="grid grid-cols-2 gap-3.5 -mt-2">
        <FieldNumber
          label="Attempts"
          value={retry.attempts}
          onChange={(n) => onPatch({ retry: { ...retry, attempts: n } })}
        />
        <FieldNumber
          label="Backoff (ms)"
          value={retry.backoffMs}
          onChange={(n) => onPatch({ retry: { ...retry, backoffMs: n } })}
        />
      </div>

      <FieldSelect
        label="On failure"
        value={step.onFailure ?? 'fail'}
        options={ON_FAILURE_OPTIONS.map((v) => ({ value: v, label: humanizeEnum(v) }))}
        onChange={(v) => onPatch({ onFailure: v as WorkflowStepOnFailure })}
      />
    </div>
  );
}

// ===== Field primitives =====

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-[12px] font-semibold text-text-2 mb-1.5">
      {children}
    </label>
  );
}

function FieldText({
  label,
  value,
  onChange,
  placeholder,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-10 px-3 rounded-[10px] border border-border-strong bg-surface text-[13.5px] outline-none focus:border-accent"
        style={{ fontFamily: mono ? 'var(--font-mono)' : 'var(--font-ui)' }}
      />
    </div>
  );
}

function FieldTextarea({
  label,
  value,
  onChange,
  rows = 3,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 rounded-[10px] border border-border-strong bg-surface text-[13.5px] outline-none focus:border-accent resize-y"
      />
    </div>
  );
}

function FieldNumber({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => {
          const n = Number(e.target.value);
          onChange(Number.isFinite(n) ? n : 0);
        }}
        className="w-full h-10 px-3 rounded-[10px] border border-border-strong bg-surface text-[13.5px] outline-none focus:border-accent"
      />
    </div>
  );
}

function FieldSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 px-3 rounded-[10px] border border-border-strong bg-surface text-[13.5px] outline-none focus:border-accent"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
